import type { ThemeConfig, ThemeTokens } from "../types";
import { deriveAccent, warnOnPoorContrast } from "./custom";
import { PRESETS } from "./presets";

const CSS_VAR: Record<keyof ThemeTokens, string> = {
  bg: "--ck-bg",
  surface: "--ck-surface",
  text: "--ck-text",
  textMuted: "--ck-text-muted",
  accent: "--ck-accent",
  accentSoft: "--ck-accent-soft",
  onAccent: "--ck-on-accent",
  secondaryBg: "--ck-secondary-bg",
  secondaryText: "--ck-secondary-text",
  border: "--ck-border",
  radius: "--ck-radius",
  radiusSm: "--ck-radius-sm",
  font: "--ck-font",
  shadow: "--ck-shadow",
  overlay: "--ck-overlay",
};

/**
 * Resolution order: preset variant -> accent-derived ramp -> `tokens` -> `darkTokens`.
 * The accent step exists so that `data-accent="#hex"` alone produces a coherent theme
 * without the site owner having to reason about hover states or text contrast.
 */
export function resolveTokens(theme: ThemeConfig, dark: boolean, debug: boolean): ThemeTokens {
  const preset = PRESETS[theme.preset ?? "porcelain"];
  const base = dark ? preset.dark : preset.light;
  const derived = theme.accent ? deriveAccent(base, theme.accent, dark) : base;

  const tokens: ThemeTokens = {
    ...derived,
    ...stripEmpty(theme.tokens),
    ...(dark ? stripEmpty(theme.darkTokens) : {}),
  };

  if (debug) warnOnPoorContrast(tokens);
  return tokens;
}

export function tokensToCss(tokens: ThemeTokens): string {
  return (Object.keys(CSS_VAR) as (keyof ThemeTokens)[])
    .map((key) => `${CSS_VAR[key]}:${tokens[key]};`)
    .join("");
}

/** True when the dark variant should be used right now. */
export function prefersDark(scheme: ThemeConfig["scheme"]): boolean {
  if (scheme === "dark") return true;
  if (scheme === "light") return false;
  return typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: dark)").matches;
}

function stripEmpty(tokens: Partial<ThemeTokens> | undefined): Partial<ThemeTokens> {
  if (!tokens) return {};
  const out: Partial<ThemeTokens> = {};
  for (const [key, value] of Object.entries(tokens)) {
    if (typeof value === "string" && value.length > 0) out[key as keyof ThemeTokens] = value;
  }
  return out;
}
